import { useState } from "react";
import {
    Typography,
    TextField,
    MenuItem,
    Button,
    Box,
    Card,
    CircularProgress,
} from "@mui/material";
import { Send as SendIcon } from "@mui/icons-material";
import COLORS from "../../../../../constants/colors";
import baseURL from "../../config";
import { toast } from "react-toastify";

const HelpCenterPage = ({ onTicketSubmitted }) => {
    const [ticket, setTicket] = useState({
        description: "",
        category: "general",
    });
    const [loading, setLoading] = useState(false);

    const handleChange = (e) => {
        setTicket({ ...ticket, [e.target.name]: e.target.value });
    };

    const handleSubmit = async () => {
        if (!ticket.description.trim()) {
            toast.error("Please provide a detailed description of your issue.");
            return;
        }

        setLoading(true);

        try {
            const response = await fetch(`${baseURL}/user/tickets`, {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(ticket),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to submit ticket. Please try again.");
            }

            toast.success("Your support ticket has been submitted successfully! We'll get back to you soon.");
            setTicket({ description: "", category: "general" });
            if (onTicketSubmitted) {
                onTicketSubmitted();
            }
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card
            sx={{
                p: { xs: 2.5, md: 4 },
                borderRadius: 2,
                boxShadow: 0,
                bgcolor: "#fff",
                textAlign: "left",
                mx: "auto",
                border: `1px solid ${COLORS.border}`,
            }}
        >
            <Typography
                variant="h5"
                fontWeight="bold"
                sx={{ textAlign: "left", color: COLORS.primary, mb: 1.5 }}
            >
                Need Assistance? Open a Support Ticket
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: "#666", textAlign: "left", lineHeight: 1.6 }}>
                We're here to help! Describe your issue below, and our dedicated support team will review your request and get back to you promptly.
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <TextField
                    fullWidth
                    select
                    label="Issue Category"
                    name="category"
                    value={ticket.category}
                    onChange={handleChange}
                    variant="outlined"
                    sx={{
                        "& .MuiOutlinedInput-root": {
                            borderRadius: "10px",
                            backgroundColor: "#fefefe",
                            "& fieldset": { borderColor: "rgba(0, 0, 0, 0.15)" },
                            "&:hover fieldset": { borderColor: COLORS.primary, borderWidth: "1.5px" },
                            "&.Mui-focused fieldset": { borderColor: COLORS.primary, borderWidth: "2px" },
                        },
                        "& .MuiInputLabel-root": { color: "#555" },
                        "& .MuiInputLabel-root.Mui-focused": { color: COLORS.primary },
                    }}
                >
                    <MenuItem value="general">General Inquiry</MenuItem>
                    <MenuItem value="technical">Technical Issue</MenuItem>
                    <MenuItem value="billing">Billing Issue</MenuItem>
                    <MenuItem value="complaint">Complaint</MenuItem>
                    <MenuItem value="feature_request">Feature Request</MenuItem>
                </TextField>
                <TextField
                    fullWidth
                    name="description"
                    label="Describe Your Issue"
                    value={ticket.description}
                    onChange={handleChange}
                    variant="outlined"
                    multiline
                    rows={7}
                    placeholder="Please provide a detailed description of your issue, including any error messages, steps you've already tried, or relevant links/screenshots. This helps us resolve your query faster."
                    sx={{
                        "& .MuiOutlinedInput-root": {
                            borderRadius: "10px",
                            backgroundColor: "#fefefe",
                            "& fieldset": { borderColor: "rgba(0, 0, 0, 0.15)" },
                            "&:hover fieldset": { borderColor: COLORS.primary, borderWidth: "1.5px" },
                            "&.Mui-focused fieldset": { borderColor: COLORS.primary, borderWidth: "2px" },
                        },
                        "& .MuiInputLabel-root": { color: "#555" },
                        "& .MuiInputLabel-root.Mui-focused": { color: COLORS.primary },
                    }}
                />
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                        sx={{
                            textTransform: "none",
                            borderRadius: 2,
                            fontWeight: "bold",
                            fontSize: "1rem",
                            px: 3.5,
                            py: 1.2,
                            boxShadow: 0,
                            transition: "all 0.3s ease-in-out",
                            "&:hover": { boxShadow: 0 },
                        }}
                    >
                        {loading ? "Submitting..." : "Submit Ticket"}
                    </Button>
                </Box>
            </Box>
        </Card>
    );
};

export default HelpCenterPage;